import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const ImageProcessing = () => {
  // NOTE: Only "Images Uploaded" shows real data. Other AI metrics are not yet implemented in backend.
  const [processingData, setProcessingData] = useState([
    {
      label: 'Images Uploaded',
      value: 0,
      color: '#6c757d',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="9" cy="9" r="2" />
          <path d="M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
        </svg>
      ),
      isImplemented: true
    },
    {
      label: 'Blurred Detected',
      value: 0,
      color: '#ffc107',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      ),
      isImplemented: false
    },
    {
      label: 'Background Removed',
      value: 0,
      color: '#dc3545',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 12l2 2 4-4" />
          <path d="M21 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
          <path d="M3 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
          <path d="M12 21c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
          <path d="M12 3c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
        </svg>
      ),
      isImplemented: false
    },
    {
      label: 'Images Enhanced',
      value: 0,
      color: '#28a745',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
        </svg>
      ),
      isImplemented: false
    }
  ]);

  const [processingRate, setProcessingRate] = useState(0);
  const [aiAccuracy, setAiAccuracy] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchImageProcessingData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/dashboard/image-processing');
        const result = response.data;

        if (result?.success && result?.data) {
          const data = result.data;

          // ✅ Functional state update (NO warning, NO loop)
          setProcessingData(prev => [
            { ...prev[0], value: data.imagesUploaded || 0 },
            { ...prev[1], value: data.blurredDetected || 0 },
            { ...prev[2], value: data.backgroundRemoved || 0 },
            { ...prev[3], value: data.imagesEnhanced || 0 }
          ]);

          setProcessingRate(data.processingRate || 0);
          setAiAccuracy(data.aiAccuracy || 0);
        }
      } catch (error) {
        console.error('Error fetching image processing data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchImageProcessingData();
  }, []); // ✅ Correct dependency array

  const maxValue = Math.max(...processingData.map(item => item.value), 1);

  return (
    <div className="dashboard-card processing-card">
      <div className="card-body">
        <div className="card-header">
          <h3 className="card-title">Image Processing & AI Insights</h3>
          <p className="card-subtitle">AI-powered image enhancement statistics</p>
        </div>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            Loading image processing data...
          </div>
        ) : (
          <>
            <div className="processing-chart">
              {processingData.map(item => (
                <div
                  key={item.label}
                  className="processing-bar-container"
                  style={{ opacity: item.isImplemented ? 1 : 0.6 }}
                >
                  <div className="processing-info">
                    <div className="processing-icon">{item.icon}</div>
                    <div className="processing-details">
                      <div className="processing-label">
                        {item.label}
                        {!item.isImplemented && (
                          <span
                            style={{
                              fontSize: '10px',
                              marginLeft: '8px',
                              padding: '2px 6px',
                              backgroundColor: '#6c757d',
                              color: '#fff',
                              borderRadius: '4px'
                            }}
                          >
                            Coming Soon
                          </span>
                        )}
                      </div>
                      <div className="processing-value">
                        {item.value.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="processing-bar-track">
                    <div
                      className="processing-bar"
                      style={{ width: `${(item.value / maxValue) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="processing-stats">
              <div className="stat-item">
                <div className="stat-value">{processingRate}%</div>
                <div className="stat-label">Processing Rate</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{aiAccuracy}%</div>
                <div className="stat-label">AI Accuracy</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImageProcessing;
